<template>
  <div>
    <h1 class="mb-6 text-2xl text-gray-777 font-roboto uppercase">Atestados</h1>
    <data-table
      v-if="loadTable"
      v-model:columns="columns"
      :route="'/patients/certificates/' + patient.id"
      no-data-label="Nenhuma atestado cadastrado"
      no-result-label="Nenhuma atestado encontrado"
    >
      <template #table.cell.content.actions="{ value, row }">
        <div class="inline-grid grid-cols-1 gap-4 ">
          <a :href="`/patients/certificates/download/` + row.id" target="_blank"><font-awesome-icon icon="fa-eye" class="text-green-500 text-lg cursor-pointer" /></a>
        </div>
      </template>
      <template #grid.content.body.actions="{ value, row }">
        <div class="inline-grid grid-cols-1 gap-4 ">
          <a :href="`/patients/certificates/download/` + row.id" target="_blank"><font-awesome-icon icon="fa-eye" class="text-green-500 text-lg cursor-pointer" /></a>
        </div>
      </template>

      <template #after.reload-button>
        <button v-if="user.role == 2" class="flex items-end font-roboto font-medium btn-blue uppercase" @click.prevent="openModalEdit({})">
          <font-awesome-icon icon="fa-plus" class="text-white text-lg mr-0 lg:mr-2" /><span class="hidden lg:block"> Adicionar Atestado</span>
        </button>
      </template>
    </data-table>
  </div>
  <modal-edit-steps ref="modalEdit"  :steps="stepItems">
    <template #content.generate_certificate="{item}">
      <date-time-input v-model="item.date" :error="item.errors.date" class="lg:pr-6 w-full lg:w-1/2" label="Data de emissão" />
      <select-input v-model="item.type" :error="item.errors.type" class="w-full mt-6 lg:mt-0 lg:w-1/2" label="Tipo do atestado">
        <option v-for="(value, name, index) in this.certificate_types()" :key="name" :value="name">{{ value }}</option>
      </select-input>
      <div class="w-full flex flex-wrap">
        <switch-input v-model="item.authorized" class="lg:pr-6 w-full mt-6 lg:w-1/3" label="Autorizado pelo paciente" labelChecked="Sim" labelUnchecked="Não" />
        <select-input v-if="item.authorized" v-model="item.cid" :error="item.errors.cid" class="w-full mt-6 lg:w-2/3" label="CID">
          <option v-for="(value, name, index) in this.certificate_cids()" :key="name" :value="name">{{ value }}</option>
        </select-input>
      </div>
      <text-input v-if="item.type == 'saude'" v-model="item.period" :error="item.errors.period" class="mt-6 w-full" label="Tempo de afastamento" />
      <date-input v-if="item.type == 'comparecimento'" v-model="item.consultation_date" :error="item.errors.consultation_date" class="lg:pr-6 mt-6 w-full lg:w-1/3" label="Data" />
      <time-input v-if="item.type == 'comparecimento'" v-model="item.time_start" :error="item.errors.time_start" class="lg:pr-6 mt-6 w-full lg:w-1/3" label="Hora inicial" />
      <time-input v-if="item.type == 'comparecimento'" v-model="item.time_end" :error="item.errors.time_end" class="mt-6 w-full lg:w-1/3" label="Hora final" />
      <vue-select-input v-if="user.role == 1" :getOptionLabel="(professional) => professional.user?.name" v-model="item.professional" :options="this.professionals()" :error="item.errors[`professional.id`]" class="mt-6 w-full" label="Profissional" text="nenhum profissional encontrado" />
      <textarea-input v-model="item.description" :error="item.errors.description" class="mt-6 w-full" label="Observações" />
    </template>
    <template #content.download="{item}">
      <div class="w-full">
        Faça o download do atestado e assine o documento digitalmente:
      </div>
      <div class="w-full flex justify-center mt-5">
        <button type="button" class="px-6 py-2.5 bg-blue-600 text-white font-roboto font-medium text-xs leading-tight uppercase rounded shadow-md ml-1 hover:bg-blue-700 hover:shadow-lg focus:bg-blue-700 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-blue-800 active:shadow-lg transition duration-150 ease-in-out" @click.prevent="download(item)">Download</button>
      </div>
    </template>
    <template #content.upload="{item}">
      <div class="w-full">
        Faça o upload do documento assinado digitalmente:
      </div>
      <file-input v-model="item.file" :error="item.errors.file" class="mt-6 w-full" type="file" accept="application/pdf" /> 
    </template>
    <template #content.success>
      <div class="w-full text-center">
        <font-awesome-icon icon="fa-regular fa-circle-check" class="text-green-600 text-7xl mr-0 lg:mr-2" />
      </div>
      <div class="w-full text-center mt-5 uppercase">
        O documento foi enviado para o paciente
      </div>
    </template>
  </modal-edit-steps>
</template>

<script>
import { computed } from 'vue'
import { usePage } from '@inertiajs/inertia-vue3'
import format from 'date-fns/format';
import LoadingButton from '@/Shared/LoadingButton'
import ModalEdit from '@/Shared/ModalEdit'
import ModalEditSteps from '@/Shared/ModalEditSteps'
import DataTable from '@/Shared/DataTable'
import SwitchInput from '@/Shared/SwitchInput'
import TextInput from '@/Shared/TextInput'
import VueSelectInput from '@/Shared/VueSelectInput'
import DateTimeInput from '@/Shared/DateTimeInput'
import TextareaInput from '@/Shared/TextareaInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import TimeInput from '@/Shared/TimeInput'
import DateInput from '@/Shared/DateInput'

export default {
  inject: ['patient', 'professionals', 'certificate_types', 'certificate_cids'],
  components: {
    LoadingButton,
    ModalEdit,
    ModalEditSteps,
    DataTable,
    SwitchInput,
    TextInput,
    VueSelectInput,
    DateTimeInput,
    TextareaInput,
    FileInput,
    SelectInput,
    TimeInput,
    DateInput,
  },
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },
  data() {
    return {
      loadTable: true,
      stepItems: [
        {
          idStep: 'generate_certificate',
          title: 'Gerar atestado',
          submitCallback: (item, callback) => {
            let success = false;
            item.post(`/patients/certificates/validate-step-one`, {
                forceFormData: true,
                onFinish: () => {
                  let success = false;
                  if(!Object.keys(item.errors).length) {
                      success = true;
                  }
                  callback(success);
                }
            });
            return success;
          }
        },
        {
          idStep: 'download',
          title: 'Download do atestado',
        },
        {
          idStep: 'upload',
          title: 'Envie o documento assinado digitalmente',
          submitCallback: (item, callback) => {
            let success = false;
            item.post(`/patients/certificates/` + this.patient.id, {
                forceFormData: true,
                onFinish: () => {
                  let success = false;
                  if(!Object.keys(item.errors).length) {
                      success = true;
                      this.reloadTable();
                  }
                  callback(success);
                }
            });
            return success;
          }
        },
        {
          idStep: 'success',
          title: 'Atestado enviado para o paciente',
        },
      ],
      columns: [
        {
          uniqid: 'certificate_date',
          label: 'Data de Emissão',
          field: 'date',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => format(new Date(row.date), 'dd/MM/yyy HH:mm'),
        },
        {
          uniqid: 'certificate_type',
          label: 'Tipo',
          field: 'type',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => val ? row.type_label : ''
        },
        {
          uniqid: 'certificate_description',
          label: 'Observação',
          field: 'description',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm overflow-hidden',
          headerClass: 'p-4 text-left',
          format: (val, row) => val ? val.substr(0, 50) : ''
        },
        {
          uniqid: 'certificate_professional',
          label: 'Profissional',
          field: 'professional_user.name',
          visible: true,
          sortable: true,
          sortOrder: 'asc',
          classes: 'px-4 py-3 border-b text-sm',
          headerClass: 'p-4 text-left',
          format: (val, row) => row.id ? row.professional.user.name : ''
        },
        {
          uniqid: 'actions',
          label: 'Ações',
          field: 'id',
          visible: true,
          align: 'center',
          classes: 'py-3 border-b',
          headerClass: 'p-4',
        },
      ],
    }
  },
  methods: {
    openModalEdit(item) {
      this.$refs.modalEdit.openModalEdit({
        id: item.id,
        description: item.description,
        date: item.date,
        type: item.type,
        period: item.period,
        consultation_date: item.consultation_date,
        time_start: item.time_start,
        time_end: item.time_end,
        cid: item.cid,
        code: item.code,
        file: item.file,
        professional: item.professional ?? null,
        authorized: item.authorized ?? false,
      });
    },
    reloadTable() {
      this.loadTable = false;
      setTimeout(() => this.loadTable = true, 10);
    },
    download(item) {
      let data = {
        _token: this.$page.props.csrf_token,
        type_document: 'certificate',
        date: item.date,
        description: item.description,
        type: item.type,
        period: item.period,
        consultation_date: item.consultation_date,
        time_start: item.time_start,
        time_end: item.time_end,
        cid: item.cid,
        code: item.code,
        authorized: item.authorized ? '1' : '',
        professional_id: item.professional?.id,
        patient_id: this.patient.id,
      };
      let url = `/patients/pdf/generate`;
      this.openWindowWithPost(url, data);
    },
    openWindowWithPost(url, data) {
      var form = document.createElement("form");
      form.target = "_blank";
      form.method = "POST";
      form.action = url;
      form.style.display = "none";

      for (var key in data) {
          var input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = typeof(data[key]) != 'undefined' ? data[key] : '';
          form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
  },
}
</script>